'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useCartStore } from 'components/shared/Cart';
import type { CartAppliedGiftCard } from 'lib/commerce/cart/types';
import { formatGiftingPrice } from 'lib/gifting/format';
import type { Currency } from 'utils/Currency';
import styles from './GiftCardAppliedBanner.module.css';

/** "Gift card ••••1234 applied. $479 comes off at checkout." per card on the cart. */
export function GiftCardAppliedBanner() {
  const t = useTranslations('cart');
  const locale = useLocale();
  const cards = useCartStore(
    (s) => s.cart?.appliedGiftCards as CartAppliedGiftCard[] | undefined,
  );
  if (!cards?.length) return null;

  const money = (m: { amount: string; currencyCode: string }) =>
    formatGiftingPrice(
      Number(m.amount),
      m.currencyCode.toLowerCase() as Currency,
      locale,
    );

  return (
    <div className={styles.banner} role="status">
      {cards.map((card) => {
        // Before anything is in the cart nothing is used yet: show the balance.
        const used = Number(card.amountUsed?.amount) > 0;
        return (
          <p key={card.lastCharacters} className={styles.text}>
            {used
              ? t('sharedCart.giftCard.bannerUsed', {
                  last4: card.lastCharacters,
                  amount: money(card.amountUsed),
                })
              : t('sharedCart.giftCard.bannerUpTo', {
                  last4: card.lastCharacters,
                  amount: money(card.balance),
                })}
          </p>
        );
      })}
    </div>
  );
}
